import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { salvarVigenciaValorHoraPj } from '~/Services/Service';
import {
  PERCENTUAL_RETENCAO_DEFAULT,
  TIPO_REMUNERACAO_FIXO,
  TIPO_REMUNERACAO_HORA,
} from '../utils/formatters';

const vazio = {
  competenciaInicio: '',
  competenciaFim: '',
  tipoRemuneracao: TIPO_REMUNERACAO_HORA,
  valorHora: '',
  valorFixo: '',
  horasBase: '',
  percentual13: PERCENTUAL_RETENCAO_DEFAULT,
  percentual14: PERCENTUAL_RETENCAO_DEFAULT,
  percentualFundo: PERCENTUAL_RETENCAO_DEFAULT,
};

const ModalVigenciaValorHora = ({ show, onHide, codigoPj, vigencia, onSalvo }) => {
  const ehEdicao = !!(vigencia && vigencia.codigo);
  // DW-0982 — Melhoria Valor Fixo: vigências geradas por contrato bloqueiam troca de
  // modalidade aqui (a modalidade é definida no modal de Contrato). Datas e valores
  // ainda são editáveis para cobrir correções pontuais (ex.: digitação errada do valor
  // no momento da assinatura).
  const origemContrato = !!(vigencia && vigencia.origem === 'contrato');
  const [form, setForm] = useState(vazio);
  const [erros, setErros] = useState({});
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!show) return;
    if (vigencia) {
      setForm({
        competenciaInicio: vigencia.data_inicio_vigente
          ? String(vigencia.data_inicio_vigente).slice(0, 7)
          : '',
        competenciaFim: vigencia.data_fim_vigencia
          ? String(vigencia.data_fim_vigencia).slice(0, 7)
          : '',
        tipoRemuneracao: vigencia.tipo_remuneracao || TIPO_REMUNERACAO_HORA,
        valorHora: vigencia.valor_hora != null ? Number(vigencia.valor_hora) : '',
        valorFixo: vigencia.valor_fixo != null ? Number(vigencia.valor_fixo) : '',
        horasBase: vigencia.horas_base != null ? Number(vigencia.horas_base) : '',
        percentual13: vigencia.percentual_13 != null ? Number(vigencia.percentual_13) : PERCENTUAL_RETENCAO_DEFAULT,
        percentual14: vigencia.percentual_14 != null ? Number(vigencia.percentual_14) : PERCENTUAL_RETENCAO_DEFAULT,
        percentualFundo: vigencia.percentual_fundo != null ? Number(vigencia.percentual_fundo) : PERCENTUAL_RETENCAO_DEFAULT,
      });
    } else {
      setForm(vazio);
    }
    setErros({});
  }, [show, vigencia]);

  const alterar = event => {
    const { name, value } = event.target;
    setForm(atual => ({ ...atual, [name]: value }));
    setErros(atuais => ({ ...atuais, [name]: null }));
  };

  const trocarModalidade = (tipo) => {
    // Limpa os campos da modalidade desativada para evitar enviar lixo na submissão.
    setForm(atual => ({
      ...atual,
      tipoRemuneracao: tipo,
      valorHora: tipo === TIPO_REMUNERACAO_HORA ? atual.valorHora : '',
      valorFixo: tipo === TIPO_REMUNERACAO_FIXO ? atual.valorFixo : '',
      horasBase: tipo === TIPO_REMUNERACAO_FIXO ? atual.horasBase : '',
    }));
    setErros({});
  };

  const validar = () => {
    const novos = {};
    if (!form.competenciaInicio || !/^\d{4}-\d{2}$/.test(form.competenciaInicio)) {
      novos.competenciaInicio = 'Competência início obrigatória (AAAA-MM).';
    }
    if (form.competenciaFim && !/^\d{4}-\d{2}$/.test(form.competenciaFim)) {
      novos.competenciaFim = 'Use o formato AAAA-MM.';
    }
    if (form.competenciaFim && form.competenciaInicio && form.competenciaFim < form.competenciaInicio) {
      novos.competenciaFim = 'Competência fim deve ser maior ou igual à início.';
    }
    if (form.tipoRemuneracao === TIPO_REMUNERACAO_HORA) {
      if (!form.valorHora || Number(form.valorHora) <= 0) {
        novos.valorHora = 'Valor-hora deve ser maior que zero.';
      }
    } else {
      if (!form.valorFixo || Number(form.valorFixo) <= 0) {
        novos.valorFixo = 'Valor fixo deve ser maior que zero.';
      }
      if (!form.horasBase || Number(form.horasBase) <= 0) {
        novos.horasBase = 'Horas base deve ser maior que zero.';
      }
    }
    // Percentuais 0..100 (DW-0982 — Melhoria Percentuais por Contrato).
    [['percentual13', '13ª NF'], ['percentual14', '14ª NF'], ['percentualFundo', 'Fundo']]
      .forEach(([campo, nome]) => {
        const valor = form[campo];
        if (valor === '' || valor == null || isNaN(Number(valor))) {
          novos[campo] = `Percentual ${nome} obrigatório.`;
        } else if (Number(valor) < 0 || Number(valor) > 100) {
          novos[campo] = `Percentual ${nome} deve estar entre 0 e 100.`;
        }
      });
    setErros(novos);
    return Object.keys(novos).length === 0;
  };

  const handleSalvar = async event => {
    event.preventDefault();
    if (!validar()) return;

    setSalvando(true);
    try {
      const ehFixo = form.tipoRemuneracao === TIPO_REMUNERACAO_FIXO;
      const payload = {
        codigo_pj: codigoPj,
        codigo: ehEdicao ? vigencia.codigo : null,
        competencia_inicio: form.competenciaInicio,
        competencia_fim: form.competenciaFim ? form.competenciaFim : null,
        tipo_remuneracao: form.tipoRemuneracao,
        valor_hora: ehFixo ? null : Number(form.valorHora),
        valor_fixo: ehFixo ? Number(form.valorFixo) : null,
        horas_base: ehFixo ? Number(form.horasBase) : null,
        percentual_13: Number(form.percentual13),
        percentual_14: Number(form.percentual14),
        percentual_fundo: Number(form.percentualFundo),
      };
      const resposta = await salvarVigenciaValorHoraPj(payload);
      toast.success(resposta && resposta.mensagem ? resposta.mensagem : 'Vigência salva com sucesso.');
      if (onSalvo) onSalvo();
      onHide();
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao salvar vigência.');
    } finally {
      setSalvando(false);
    }
  };

  const RequiredLabel = ({ children }) => (
    <Form.Label>
      {children}
      <span className="hpj-required">*</span>
    </Form.Label>
  );

  const FieldError = ({ name }) => (
    erros[name] ? <div className="hpj-field-error">{erros[name]}</div> : null
  );

  const ehFixo = form.tipoRemuneracao === TIPO_REMUNERACAO_FIXO;

  return (
    <Modal
      className="hpj-modal hpj-modal-vigencia"
      show={show}
      onHide={onHide}
      centered
    >
      <Modal.Header closeButton>
        <div>
          <div className="hpj-modal-eyebrow">Histórico</div>
          <h5 className="hpj-modal-title mb-0">
            {ehEdicao ? 'Editar Vigência' : 'Adicionar Vigência'}
          </h5>
        </div>
      </Modal.Header>
      <Form onSubmit={handleSalvar}>
        <Modal.Body>
          {origemContrato && (
            <Alert variant="warning" className="mb-3">
              <strong>⚠️ Vigência gerada por contrato.</strong> Alterações aqui não voltam para o contrato; ajuste com cuidado. A modalidade só pode ser alterada pelo modal de Contrato.
            </Alert>
          )}

          <div className="hpj-form-section">
            <Form.Group>
              <RequiredLabel>Modalidade</RequiredLabel>
              <div>
                <Form.Check
                  inline
                  type="radio"
                  id="modalidadeHora"
                  label="Por Hora"
                  name="tipoRemuneracao"
                  checked={form.tipoRemuneracao === TIPO_REMUNERACAO_HORA}
                  disabled={origemContrato}
                  onChange={() => trocarModalidade(TIPO_REMUNERACAO_HORA)}
                />
                <Form.Check
                  inline
                  type="radio"
                  id="modalidadeFixo"
                  label="Valor Fixo"
                  name="tipoRemuneracao"
                  checked={form.tipoRemuneracao === TIPO_REMUNERACAO_FIXO}
                  disabled={origemContrato}
                  onChange={() => trocarModalidade(TIPO_REMUNERACAO_FIXO)}
                />
              </div>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className={erros.competenciaInicio ? 'has-error' : ''}>
                  <RequiredLabel>Competência início</RequiredLabel>
                  <Form.Control
                    name="competenciaInicio"
                    type="month"
                    value={form.competenciaInicio}
                    onChange={alterar}
                  />
                  <FieldError name="competenciaInicio" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className={erros.competenciaFim ? 'has-error' : ''}>
                  <Form.Label>Competência fim</Form.Label>
                  <Form.Control
                    name="competenciaFim"
                    type="month"
                    value={form.competenciaFim}
                    onChange={alterar}
                    placeholder="Vazio = vigente"
                  />
                  <FieldError name="competenciaFim" />
                </Form.Group>
              </Col>
            </Row>

            {ehFixo ? (
              <Row>
                <Col md={4}>
                  <Form.Group className={erros.valorFixo ? 'has-error' : ''}>
                    <RequiredLabel>Valor fixo (R$)</RequiredLabel>
                    <Form.Control
                      name="valorFixo"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={form.valorFixo}
                      onChange={alterar}
                    />
                    <FieldError name="valorFixo" />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className={erros.horasBase ? 'has-error' : ''}>
                    <RequiredLabel>Horas base / mês</RequiredLabel>
                    <Form.Control
                      name="horasBase"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={form.horasBase}
                      onChange={alterar}
                    />
                    <FieldError name="horasBase" />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  {/* ADR-013: Valor Hora pactuado (readonly) — Valor_Fixo / Horas_Base. */}
                  <Form.Group>
                    <Form.Label>Valor-hora (R$)</Form.Label>
                    <Form.Control
                      type="text"
                      readOnly
                      value={
                        Number(form.valorFixo) > 0 && Number(form.horasBase) > 0
                          ? (Number(form.valorFixo) / Number(form.horasBase)).toFixed(2)
                          : ''
                      }
                      placeholder="—"
                      title="Calculado: Valor fixo ÷ Horas base"
                      style={{ background: '#f5f5f5' }}
                    />
                    <Form.Text className="text-muted">
                      Calculado: Valor fixo ÷ Horas base
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            ) : (
              <Row>
                <Col md={6}>
                  <Form.Group className={erros.valorHora ? 'has-error' : ''}>
                    <RequiredLabel>Valor-hora (R$)</RequiredLabel>
                    <Form.Control
                      name="valorHora"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={form.valorHora}
                      onChange={alterar}
                    />
                    <FieldError name="valorHora" />
                  </Form.Group>
                </Col>
              </Row>
            )}

            {/* DW-0982 — Melhoria Percentuais por Contrato: 3 percentuais aplicados sobre o Valor NF. */}
            <div className="hpj-form-divider" />
            <div className="hpj-form-subtitle">Percentuais de retenção sobre o Valor NF</div>
            <Row>
              <Col md={4}>
                <Form.Group className={erros.percentual13 ? 'has-error' : ''}>
                  <RequiredLabel>13ª NF (%)</RequiredLabel>
                  <Form.Control
                    name="percentual13"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={form.percentual13}
                    onChange={alterar}
                  />
                  <FieldError name="percentual13" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className={erros.percentual14 ? 'has-error' : ''}>
                  <RequiredLabel>14ª NF (%)</RequiredLabel>
                  <Form.Control
                    name="percentual14"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={form.percentual14}
                    onChange={alterar}
                  />
                  <FieldError name="percentual14" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className={erros.percentualFundo ? 'has-error' : ''}>
                  <RequiredLabel>Fundo (%)</RequiredLabel>
                  <Form.Control
                    name="percentualFundo"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={form.percentualFundo}
                    onChange={alterar}
                  />
                  <FieldError name="percentualFundo" />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            className="hpj-btn hpj-btn-outline"
            onClick={onHide}
            disabled={salvando}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            className="hpj-btn hpj-btn-primary"
            variant="primary"
            disabled={salvando}
          >
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ModalVigenciaValorHora;
